// ─── Révision du code de la route (types et calculs purs) ──────────────────────────────────────

export type RoadCodeThemeKey =
  | 'circulation'
  | 'conducteur'
  | 'route'
  | 'usagers'
  | 'notions'
  | 'secours'
  | 'vehicule'
  | 'mecanique'
  | 'securite'
  | 'environnement';

export type SeriesMode = 'thematic' | 'training' | 'exam' | 'mistakes';
export type SeriesFeedback = 'each' | 'end';
export type ThemeLevel = 'not_started' | 'to_work' | 'to_improve' | 'mastered';
export type Letter = 'A' | 'B' | 'C' | 'D';

export type RoadCodeAccess = { status: 'none' | 'active' | 'ended'; endsAt: string | null };

export type ThemeScore = {
  theme: RoadCodeThemeKey;
  label: string;
  examQuestions: number;
  /** Questions de la banque dans ce thème. */
  questions: number;
  /** Réponses qui fondent la moyenne (20 dernières au plus). */
  answers: number;
  score: number | null;
  level: ThemeLevel;
};

export type SeriesSummary = {
  id: string;
  mode: SeriesMode;
  theme: RoadCodeThemeKey | null;
  total: number;
  correct: number | null;
  passed: boolean | null;
  finishedAt: string;
};

export type RoadCodeProgress = {
  themes: ThemeScore[];
  week: { minutes: number };
  dayStreak: number;
  exams: { taken: number; streak: number; goal: number; ready: boolean };
  mistakes: number;
  current: { id: string; mode: SeriesMode; theme: RoadCodeThemeKey | null; answered: number; total: number; startedAt: string } | null;
  recent: SeriesSummary[];
  lastActivityAt: string | null;
};

export type RoadCodeOverview = {
  access: RoadCodeAccess;
  bank: { questions: number; lessons: number };
  themes: Array<{ key: RoadCodeThemeKey; label: string; examQuestions: number }>;
  progress: RoadCodeProgress | null;
};

export type RoadCodeMedia = { file: string; alt: string; kind: 'image' | 'video' };

/** Source d'une règle : article du Code de la route (lien Légifrance) ou autre référence. */
export type ContentSource = { label: string; url: string | null };

export type AnswerView = {
  selected: Letter[];
  correct?: boolean;
  expected?: Letter[];
  explanation?: string;
  lesson?: string | null;
  sources?: ContentSource[];
};

export type QuestionView = {
  position: number;
  id: string;
  theme: RoadCodeThemeKey;
  intro: string | null;
  parts: Array<{ prompt: string; answers: Array<{ letter: Letter; text: string }> }>;
  multiple: boolean;
  media: RoadCodeMedia | null;
  answer: AnswerView | null;
  missing?: undefined;
};

export type MissingQuestion = { position: number; id: string; missing: true };

export type SeriesResult = {
  total: number;
  correct: number;
  mistakes: number;
  passMark: number | null;
  passed: boolean | null;
  byTheme: Array<{ theme: RoadCodeThemeKey; label: string; total: number; correct: number }>;
};

export type RoadCodeSeries = {
  id: string;
  mode: SeriesMode;
  theme: RoadCodeThemeKey | null;
  feedback: SeriesFeedback;
  status: 'in_progress' | 'finished' | 'abandoned';
  startedAt: string;
  finishedAt: string | null;
  secondsPerQuestion: number | null;
  total: number;
  answered: number;
  questions: Array<QuestionView | MissingQuestion>;
  result: SeriesResult | null;
};

export type AnswerReply = {
  position: number;
  answer: AnswerView;
  finished: boolean;
  result: SeriesResult | null;
};

export type LessonBlock =
  | { type: 'text' | 'tip' | 'warning'; text: string }
  | { type: 'list'; items: string[] }
  | { type: 'media'; file: string; alt: string; kind: 'image' | 'video' };

export type LessonCatalog = {
  themes: Array<{
    theme: RoadCodeThemeKey;
    label: string;
    lessons: Array<{ id: string; title: string; sections: number; read: number }>;
  }>;
};

export type LessonView = {
  id: string;
  theme: RoadCodeThemeKey;
  label: string;
  title: string;
  sections: Array<{ title: string; blocks: LessonBlock[] }>;
  sources?: ContentSource[];
  read: number;
  next: { id: string; title: string } | null;
};

/** Ce que l'élève lance depuis l'accueil. */
export type SeriesStart = { mode: SeriesMode; theme?: RoadCodeThemeKey; feedback?: SeriesFeedback };

export const MODE_LABELS: Record<SeriesMode, string> = {
  thematic: 'Série thématique',
  training: 'Entraînement',
  exam: 'Examen blanc',
  mistakes: 'Mes erreurs',
};

/** Titre d'une série : son type, et son thème pour une série thématique. */
export function seriesTitle(mode: SeriesMode, theme: RoadCodeThemeKey | null, themes: Array<{ key: string; label: string }>) {
  const label = theme ? themes.find((t) => t.key === theme)?.label : null;
  return mode === 'thematic' && label ? label : MODE_LABELS[mode];
}

export const LEVEL_ORDER: ThemeLevel[] = ['to_work', 'to_improve', 'mastered', 'not_started'];

export const LEVEL_LABELS: Record<ThemeLevel, string> = {
  to_work: 'Thèmes à travailler',
  to_improve: 'Thèmes à perfectionner',
  mastered: 'Thèmes maîtrisés',
  not_started: 'Thèmes à découvrir',
};

/** Couleur de la note d'un thème (pastille de la charte). */
export function levelTone(level: ThemeLevel): 'danger' | 'warn' | 'ok' | 'mute' {
  if (level === 'to_work') return 'danger';
  if (level === 'to_improve') return 'warn';
  if (level === 'mastered') return 'ok';
  return 'mute';
}

/** Thèmes rangés par niveau (à travailler d'abord), du plus faible au plus fort dans chaque niveau. */
export function themesByLevel(themes: ThemeScore[]) {
  return LEVEL_ORDER.map((level) => ({
    level,
    label: LEVEL_LABELS[level],
    themes: themes.filter((t) => t.level === level).sort((a, b) => (a.score ?? 0) - (b.score ?? 0)),
  })).filter((group) => group.themes.length > 0);
}

/** Thèmes à proposer en premier : les plus faibles déjà travaillés, puis ceux jamais ouverts. */
export function weakestThemes(themes: ThemeScore[], count = 3) {
  const worked = themes.filter((t) => t.score !== null && t.level !== 'mastered').sort((a, b) => a.score! - b.score!);
  const fresh = themes.filter((t) => t.score === null && t.questions > 0);
  return [...worked, ...fresh].slice(0, count);
}

/**
 * Lettre touchée : une question à une seule réponse remplace le choix, une question à plusieurs
 * réponses le coche ou le décoche. Sélection toujours rangée de A à D.
 */
export function toggleLetter(selected: Letter[], letter: Letter, multiple: boolean): Letter[] {
  if (!multiple) return selected.length === 1 && selected[0] === letter ? [] : [letter];
  const next = selected.includes(letter) ? selected.filter((l) => l !== letter) : [...selected, letter];
  return (['A', 'B', 'C', 'D'] as Letter[]).filter((l) => next.includes(l));
}

/** Lettres de la question, dans l'ordre. */
export function lettersOf(question: QuestionView): Letter[] {
  return question.parts.flatMap((part) => part.answers.map((answer) => answer.letter));
}

/** Prochaine question à poser (première sans réponse), ou `null` si la série est finie. */
export function nextPosition(series: Pick<RoadCodeSeries, 'questions'>): number | null {
  const next = series.questions.find((q) => !('missing' in q && q.missing) && !(q as QuestionView).answer);
  if (next) return next.position;
  const missing = series.questions.find((q) => 'missing' in q && q.missing);
  return missing ? missing.position : null;
}

/** Série mise à jour après la réponse enregistrée. */
export function withAnswer(series: RoadCodeSeries, reply: AnswerReply): RoadCodeSeries {
  return {
    ...series,
    answered: Math.max(series.answered, reply.position + 1),
    status: reply.finished ? 'finished' : series.status,
    result: reply.result ?? series.result,
    questions: series.questions.map((q) => (q.position === reply.position && !('missing' in q && q.missing) ? { ...q, answer: reply.answer } : q)),
  };
}

/** « 12 min », « 1 h 05 ». */
export function minutesLabel(minutes: number) {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `${hours} h ${String(minutes % 60).padStart(2, '0')}`;
}

/** Lettres séparées par des virgules : « A, C ». */
export function lettersLabel(letters: Letter[]) {
  return letters.length ? letters.join(', ') : 'aucune';
}

/** Message d'un refus de l'API, ou le texte de repli. */
export function apiMessage(error: unknown, fallback: string): string {
  const message = (error as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
  if (typeof message === 'string' && message.trim()) return message;
  return fallback;
}

/** Statut HTTP d'une erreur de l'API (0 : réseau). */
export function apiStatus(error: unknown): number {
  return (error as { response?: { status?: number } })?.response?.status ?? 0;
}

/** Accueil lu, ou aucun accès (réponse inattendue) : l'onglet reste caché. */
export function roadCodeOverviewOf(data: unknown): RoadCodeOverview | null {
  if (!data || typeof data !== 'object') return null;
  const value = data as Partial<RoadCodeOverview>;
  if (!value.access || !['none', 'active', 'ended'].includes(value.access.status)) return null;
  return {
    access: value.access,
    bank: value.bank ?? { questions: 0, lessons: 0 },
    themes: Array.isArray(value.themes) ? value.themes : [],
    progress: value.progress ?? null,
  };
}
